import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Search, MapPin, Star, Phone, Clock, Navigation as NavIcon, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = {
  width: '100%',
  height: '250px',
  borderRadius: '1rem'
};

// Calculate distance between two coordinates in miles
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const Courses = () => {
  const { user } = useAuth();
  const [searchLocation, setSearchLocation] = useState(user?.location || '');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchedOnce, setSearchedOnce] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 30.2672, lng: -97.7431 }); // Austin default
  const [userCoords, setUserCoords] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseDetails, setCourseDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Convert miles to meters for API
  const radiusMeters = (user?.travel_distance || 25) * 1609;

  const handleSearch = useCallback(async () => {
    const locationToSearch = searchLocation.trim() || user?.location;
    if (!locationToSearch) {
      toast.error('Please enter a location to search');
      return;
    }

    setLoading(true);
    setSearchedOnce(true);
    try {
      const response = await axios.get(`${API}/golf-courses/search`, {
        params: {
          location: locationToSearch,
          radius: radiusMeters
        }
      });
      
      const searchCoords = response.data.location ? {
        lat: response.data.location.lat,
        lng: response.data.location.lng
      } : null;
      
      setUserCoords(searchCoords);
      
      if (searchCoords) {
        setMapCenter(searchCoords);
      }
      
      // Add distance to each course and sort by distance
      let coursesWithDistance = response.data.courses.map(course => ({
        ...course,
        distance: searchCoords ? calculateDistance(
          searchCoords.lat, searchCoords.lng,
          course.lat, course.lng
        ) : null
      }));
      
      // Sort by distance (closest first)
      coursesWithDistance.sort((a, b) => (a.distance || 999) - (b.distance || 999));
      
      setCourses(coursesWithDistance);
      setSelectedCourse(null);
      setCourseDetails(null);
      
      if (coursesWithDistance.length === 0) {
        toast.info('No golf courses found in this area. Try a larger search radius in Settings.');
      } else {
        toast.success(`Found ${coursesWithDistance.length} golf courses nearby!`);
      }
    } catch (error) {
      toast.error('Failed to search for courses');
    } finally {
      setLoading(false);
    }
  }, [searchLocation, user?.location, radiusMeters]);

  // Auto-search on mount if user has location
  useEffect(() => {
    if (user?.location && !searchedOnce) {
      handleSearch();
    }
  }, [user?.location, searchedOnce, handleSearch]);

  const handleCourseClick = async (course) => {
    setSelectedCourse(course);
    setDetailsLoading(true);
    
    try {
      const response = await axios.get(`${API}/golf-courses/${course.place_id}`);
      setCourseDetails(response.data);
    } catch (error) {
      /* silent */
    } finally {
      setDetailsLoading(false);
    }
  };

  const formatDistance = (miles) => {
    if (miles === null || miles === undefined) return '';
    if (miles < 1) return `${(miles * 5280).toFixed(0)} ft`;
    return `${miles.toFixed(1)} mi`;
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <Navigation />

      {/* Header */}
      <div className="px-4 pt-6 pb-4 max-w-lg mx-auto">
        <h1 className="font-serif text-2xl font-bold text-foreground">Find Courses</h1>
        <p className="text-muted-foreground text-sm">
          Discover golf courses within {user?.travel_distance || 25} miles
        </p>
      </div>

      <div className="max-w-lg mx-auto px-4 space-y-4">
        {/* Search Bar */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="City, address, or zip code"
              value={searchLocation}
              onChange={(e) => setSearchLocation(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 rounded-xl h-12"
              data-testid="location-search-input"
            />
          </div>
          <Button
            onClick={handleSearch}
            disabled={loading}
            className="rounded-xl h-12 px-6 bg-primary hover:bg-primary/90"
            data-testid="search-courses-btn"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        {/* Map */}
        {GOOGLE_MAPS_API_KEY && courses.length > 0 && (
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={10}
              >
                {/* User location marker */}
                {userCoords && (
                  <Marker
                    position={userCoords}
                    icon={{
                      url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                    }}
                    title="Your search location"
                  />
                )}
                
                {courses.map((course) => (
                  <Marker
                    key={course.place_id}
                    position={{ lat: course.lat, lng: course.lng }}
                    onClick={() => handleCourseClick(course)}
                    icon={{
                      url: selectedCourse?.place_id === course.place_id
                        ? 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
                        : 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'
                    }}
                  />
                ))}
                
                {selectedCourse && (
                  <InfoWindow
                    position={{ lat: selectedCourse.lat, lng: selectedCourse.lng }}
                    onCloseClick={() => setSelectedCourse(null)}
                  >
                    <div className="p-1 max-w-xs">
                      <h3 className="font-semibold text-sm">{selectedCourse.name}</h3>
                      <p className="text-xs text-gray-600">{formatDistance(selectedCourse.distance)} away</p>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            </LoadScript>
          </div>
        )}

        {/* Results count */}
        {courses.length > 0 && (
          <p className="text-sm text-muted-foreground">
            {courses.length} courses found, sorted by distance
          </p>
        )}

        {/* Course List */}
        <div className="space-y-3">
          {!searchedOnce && (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-10 h-10 text-muted-foreground" />
              </div>
              <h2 className="font-serif text-xl font-semibold mb-2">Find Golf Courses Near You</h2>
              <p className="text-muted-foreground max-w-sm mx-auto mb-4">
                Enter your city or zip code to discover golf courses within your travel distance
              </p>
              {user?.location && (
                <Button onClick={handleSearch} className="rounded-full" data-testid="search-my-location-btn">
                  <Search className="w-4 h-4 mr-2" />
                  Search near {user.location}
                </Button>
              )}
            </div>
          )}

          {searchedOnce && courses.length === 0 && !loading && (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-10 h-10 text-muted-foreground" />
              </div>
              <h2 className="font-serif text-xl font-semibold mb-2">No Courses Found</h2>
              <p className="text-muted-foreground max-w-sm mx-auto">
                Try searching a different location or increase your travel distance in Settings
              </p>
            </div>
          )}
          
          {courses.map((course) => (
            <Card
              key={course.place_id}
              className={`cursor-pointer transition-all rounded-2xl ${
                selectedCourse?.place_id === course.place_id
                  ? 'border-primary bg-primary/5 shadow-lg'
                  : 'border-border/50 hover:border-primary/50'
              }`}
              onClick={() => handleCourseClick(course)}
              data-testid={`course-${course.place_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{course.name}</h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {course.address}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      {course.rating && (
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="text-sm font-medium">{course.rating}</span>
                          <span className="text-xs text-muted-foreground">
                            ({course.total_ratings})
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Distance Badge */}
                  {course.distance !== null && (
                    <div className="bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm font-medium">
                      {formatDistance(course.distance)}
                    </div>
                  )}
                </div>

                {/* Expanded Course Details */}
                {selectedCourse?.place_id === course.place_id && (
                  <div className="mt-4 pt-4 border-t border-border space-y-3">
                    {detailsLoading ? (
                      <div className="text-sm text-muted-foreground">Loading details...</div>
                    ) : courseDetails && (
                      <>
                        {courseDetails.phone && (
                          <p className="text-sm flex items-center gap-2">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <a href={`tel:${courseDetails.phone}`} className="hover:underline">
                              {courseDetails.phone}
                            </a>
                          </p>
                        )}
                        
                        {courseDetails.hours && (
                          <div className="text-sm">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1">
                              <Clock className="w-4 h-4" />
                              <span>Hours</span>
                            </div>
                            <div className="text-xs text-muted-foreground pl-6 space-y-0.5">
                              {courseDetails.hours.slice(0, 3).map((h, i) => (
                                <p key={i}>{h}</p>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="flex flex-col gap-2 pt-2">
                          {courseDetails.google_maps_url && (
                            <a
                              href={courseDetails.google_maps_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-muted hover:bg-muted/80 transition-colors text-sm font-medium"
                            >
                              <NavIcon className="w-4 h-4" />
                              Get Directions
                            </a>
                          )}
                          
                          {courseDetails.website ? (
                            <a
                              href={courseDetails.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-colors text-sm font-medium"
                              data-testid={`visit-website-${course.place_id}`}
                            >
                              <ExternalLink className="w-4 h-4" />
                              Visit Course Website
                            </a>
                          ) : (
                            <a
                              href={`https://www.google.com/search?q=${encodeURIComponent(course.name + ' tee times')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-muted hover:bg-muted/80 transition-colors text-sm font-medium"
                            >
                              <Search className="w-4 h-4" />
                              Search for Website
                            </a>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Courses;
